<script setup lang="ts">
import { Link } from '@inertiajs/vue3'
import { User, Calendar, Star } from 'lucide-vue-next'

interface Tutor {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  subject: string
  sessions_count: number
  rating: number
  type: 'one-on-one' | 'group'
  schedule_days: string[]
  session_day: string | null
  session_time: string | null
}

defineProps<{
  tutors: Tutor[]
}>()

function getTypeStyles(type: string): { bg: string; text: string } {
  if (type === 'one-on-one') {
    return { bg: 'bg-blue-100', text: 'text-blue-700' }
  }
  return { bg: 'bg-purple-100', text: 'text-purple-700' }
}

function formatScheduleDays(days: string[]): string {
  return days.join(', ')
}
</script>

<template>
  <section>
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-xl font-bold text-gray-900">My Tutors</h2>
      <Link href="/student/explore" class="text-blue-600 font-medium text-sm hover:underline">View All</Link>
    </div>

    <!-- Tutors Grid -->
    <div v-if="tutors.length > 0" class="grid grid-cols-2 md:grid-cols-4 gap-6">
      <Link
        v-for="tutor in tutors"
        :key="tutor.id"
        :href="`/student/tutors/${tutor.slug}`"
        class="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all cursor-pointer block"
      >
        <!-- Tutor Header -->
        <div class="flex flex-col md:flex-row items-center mb-4 gap-4">
          <div class="w-14 h-14 rounded-full border-2 border-gray-200 mr-3 overflow-hidden bg-gray-100 flex items-center justify-center">
            <img
              v-if="tutor.avatar_url"
              :src="tutor.avatar_url"
              :alt="tutor.name"
              class="w-full h-full object-cover"
            />
            <User v-else class="w-7 h-7 text-gray-400" />
          </div>
          <div class="flex-1 text-center md:text-left">
            <h3 class="font-bold text-gray-900">{{ tutor.name }}</h3>
            <p class="text-xs text-gray-500">{{ tutor.subject }}</p>
          </div>
        </div>

        <!-- Stats -->
        <div class="flex items-center justify-between text-xs text-gray-600 mb-3">
          <span class="flex items-center">
            <Calendar class="w-3 h-3 mr-1" />
            {{ tutor.sessions_count }} sessions
          </span>
          <span class="flex items-center">
            <Star class="w-3 h-3 text-yellow-500 mr-1" />
            {{ tutor.rating }}
          </span>
        </div>

        <!-- Tags -->
        <div class="flex flex-wrap items-center gap-1.5">
          <span
            :class="[
              'text-xs px-2 py-1 rounded font-semibold',
              getTypeStyles(tutor.type).bg,
              getTypeStyles(tutor.type).text
            ]"
          >
            {{ tutor.type === 'one-on-one' ? '1:1' : 'Group' }}
          </span>
          <span v-if="tutor.session_day" class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {{ tutor.session_day }}{{ tutor.session_time ? ` · ${tutor.session_time}` : '' }}
          </span>
          <span v-else-if="tutor.schedule_days.length > 0" class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {{ formatScheduleDays(tutor.schedule_days) }}
          </span>
        </div>
      </Link>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white rounded-xl border border-gray-200 p-8 text-center">
      <div class="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <User class="w-8 h-8 text-blue-600" />
      </div>
      <h3 class="text-lg font-semibold text-gray-900 mb-2">No Tutors Yet</h3>
      <p class="text-sm text-gray-500 mb-4">Subscribe to tutors to see them here</p>
      <Link
        href="/student/explore"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
      >
        Browse Tutors
      </Link>
    </div>
  </section>
</template>
